import { computed, isRef, onMounted, ref, watch, Ref, ComputedRef } from 'vue'
import 'keen-slider/keen-slider.min.css'
import KeenSlider, { KeenSliderOptions } from 'keen-slider'
import useRouter from './useRouter'

type TabValue<T> = T | Ref<T> | ComputedRef<T>

function resolveValue<T>(value: TabValue<T>): T {
    return isRef(value) ? value.value : value
}

interface TabItem {
    tabName: string
    sectionName: string
    component: any
    show?: TabValue<boolean>
}

interface useTabsParams {
    tabs: TabValue<TabItem[]>
    defaultSectionName: TabValue<string>
}

export function useTabs(params: useTabsParams) {
    const { route, router } = useRouter()
    const activeTab = ref(0)
    const tabs = computed(() => resolveValue(params.tabs).filter(tab => resolveValue(tab.show) !== false))
    const getTabIndex = (sectionName?: any) => {
        const defaultSectionName = resolveValue(params.defaultSectionName)
        const defaultTabIndex = tabs.value.findIndex(el => el.sectionName === defaultSectionName)
        const fallbackTabIndex = defaultTabIndex >= 0 ? defaultTabIndex : 0
        if (!sectionName) return fallbackTabIndex
        const tabIndex = tabs.value.findIndex(el => el.sectionName === sectionName)
        return tabIndex >= 0 ? tabIndex : fallbackTabIndex
    }
    watch(activeTab, () => {
        const sectionName = tabs.value[activeTab.value || 0]?.sectionName
        if (sectionName) {
            const nextQuery = Object.assign({}, route.value.query, { section: sectionName }) as Record<string, any>
            if (sectionName !== 'tariffs') {
                delete nextQuery.default
                delete nextQuery.period
                delete nextQuery.highlighted
            }
            router.push({
                path: route.value.path,
                query: nextQuery,
            })
        }
    })
    watch(
        () => route.value.query.section,
        () => {
            activeTab.value = getTabIndex(route.value.query.section)
        }
    )
    watch(tabs, () => {
        activeTab.value = getTabIndex(route.value.query.section)
    })

    onMounted(() => {
        activeTab.value = getTabIndex(route.value.query.section)
    })
    return { tabs, activeTab }
}
